/**
 * Platform Role Utility — client-side
 *
 * Mirror of src/server/lib/platformRole.ts for use in React components.
 * Pure functions only — no server imports.
 *
 * Role hierarchy (ascending permission level):
 *   USER < MODERATOR < ADMINISTRATOR < PLATFORM_OWNER
 */

export const ROLE_HIERARCHY = ['USER', 'MODERATOR', 'ADMINISTRATOR', 'PLATFORM_OWNER'] as const;
export type PlatformRoleType = (typeof ROLE_HIERARCHY)[number];

interface HasPlatformRole {
  platformRole?: PlatformRoleType | string | null;
}

/**
 * PLATFORM_OWNER — Koot and Doot. Absolute full access, hidden from all user lists.
 */
export function isPlatformOwner(u?: HasPlatformRole | null): boolean {
  return u?.platformRole === 'PLATFORM_OWNER';
}

/**
 * ADMINISTRATOR or PLATFORM_OWNER. Replaces the old `isAdmin` flag in UI guards.
 */
export function isAdministrator(u?: HasPlatformRole | null): boolean {
  return u?.platformRole === 'ADMINISTRATOR' || isPlatformOwner(u);
}

/**
 * MODERATOR, ADMINISTRATOR, or PLATFORM_OWNER.
 */
export function isModerator(u?: HasPlatformRole | null): boolean {
  return u?.platformRole === 'MODERATOR' || isAdministrator(u);
}

/**
 * Returns a human-readable label for a platform role.
 */
export function getRoleLabel(role?: PlatformRoleType | null): string {
  const labels: Record<PlatformRoleType, string> = {
    PLATFORM_OWNER: 'Platform Owner',
    ADMINISTRATOR: 'Administrator',
    MODERATOR: 'Moderator',
    USER: 'User',
  };
  return labels[role ?? 'USER'] ?? 'User';
}

/**
 * Returns a Tailwind color class for a role badge.
 */
export function getRoleBadgeColor(role?: PlatformRoleType | null): string {
  switch (role) {
    case 'PLATFORM_OWNER': return 'bg-purple-100 text-purple-800';
    case 'ADMINISTRATOR': return 'bg-red-100 text-red-800';
    case 'MODERATOR': return 'bg-blue-100 text-blue-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}



